/* App Studio — shared types for the App Engine cfg API (S-0005 v1).
 * Contract: BeXO /api/v2/bexo/app-engine/cfg proxied at /cm/app-engine. */

export interface Tenancy {
    org_id: string;
    app_id: string;
}

export type DefType = 'source' | 'workflow' | 'journey' | 'app';

export interface DefListItem {
    id: string;
    name?: string;
    /** Common-scope birth-kit defs are badged 'common'; app-local (incl.
     * local shadows of common ids) come back 'local'. */
    scope?: 'local' | 'common' | string;
}

/** Closed outcome envelope — every execution returns { output, outcome }. */
export interface Outcome {
    ok: boolean;
    status: string;
    [k: string]: any;
}

export interface TryWorkflowResult {
    output: any;
    outcome: Outcome;
    steps?: Record<string, {
        output: any;
        outcome: Outcome;
    }> | Array<any>;
}

export interface ValidateResult {
    valid: boolean;
    errors: string[];
    /** ADVISORY WARNINGS (locked contract) — non-blocking, one human
     * sentence each, prefixed "[code] location: …". Saves succeed with
     * warnings; only errors[] block. */
    warnings: string[];
}

/** One referrer entry from DELETE integrity's 409 / left_dangling lists. */
export interface ReferencedBy {
    type: string;
    id?: string;
    name?: string | null;
    where?: string;
}

/** app_id is NOT in the request — the engine mints it (app_<12hex>) and
 * returns it in the response as the tenancy key. */
export interface BootstrapRequest {
    name: string;
    org?: string;
    creator: {
        email: string;
        password: string;
        name?: string;
        /** Defaults to ["admin", default_role]; 'admin' is always unioned
         * in upstream; unknown role ids are seeded as empty role docs. */
        roles?: string[];
    };
    default_role: string;
}

/** Source capability — app-facing 8-key projection + origin tag. */
export interface Capability {
    id: string;
    name?: string;
    description?: string;
    operation_type?: string;
    risk_level?: string;
    origin?: 'ingested' | 'manual' | 'modified' | string;
    input?: any;
    output?: any;
    [k: string]: any;
}

export interface SourceDoc {
    source?: {
        id?: string;
        type?: string;
        name?: string;
        description?: string;
    };
    capabilities?: Capability[];
    [k: string]: any;
}

export type RunStatus = 'running' | 'waiting' | 'completed' | 'cancelled';

export interface RunHistoryEntry {
    surface?: string;
    [k: string]: any;
}

export interface RunRecord {
    id: string;
    journey: string;
    app?: string;
    run_by?: string;
    status: RunStatus | string;
    current_surface?: string;
    waiting_on?: string | null;
    context?: Record<string, any>;
    history?: RunHistoryEntry[];
    opened_from?: string | null;
    handoff_to?: string | null;
    created_at?: string | number;
    updated_at?: string | number;
}

// ── Workflow runs (A-0017 T-50: the run LEDGER in the org's Mongo) ─────────
export type WorkflowRunVia = 'engine' | 'try' | 'pilotage' | 'job' | string;

/**
 * What the ledger keeps IN PLACE OF a value it did not store (T-64): its size
 * and shape. A run that succeeded keeps this for everything it produced —
 * the data itself already lives in the app's collections. `_truncated` means
 * content WAS kept for this kind of value but this one was over the limit.
 */
export interface RunValueSummary {
    _summary: true;
    bytes: number;
    type: string;
    items?: number;
    fields?: number;
    total?: number;
    count?: number;
    _truncated?: boolean;
    note?: string;
}

export function isRunValueSummary(v: any): v is RunValueSummary {
    return !!v && typeof v === 'object' && !Array.isArray(v) && v._summary === true;
}

export interface WorkflowRunStep {
    n: number;
    id: string;
    kind: string;
    started_at?: string;
    ended_at?: string;
    duration_ms?: number;
    status: string;
    ok: boolean | null;
    error?: string | null;
    calls?: string;
    input?: any;
    output?: any;
    decisions?: any[];
}

export interface WorkflowRunRecord {
    id: string;
    org_id: string;
    app_id: string;
    workflow_id: string;
    workflow_name?: string | null;
    via: WorkflowRunVia;
    user_id?: string | null;
    action?: string | null;
    job_run_id?: string | null;
    status: string;
    ok: boolean | null;
    error?: string | null;
    started_at: string;
    ended_at?: string | null;
    duration_ms?: number | null;
    step_count?: number;
    /** When Mongo may delete this record — 7 days after a success, 30 after a failure. */
    expires_at?: string | null;
    /** Only on get-one — list rows omit these. `output` (and a step's
     *  input/output) is a RunValueSummary unless the run/step failed. */
    input?: any;
    output?: any;
    steps?: WorkflowRunStep[];
}

export interface WorkflowRunFilters {
    workflow_id?: string;
    workflow_name?: string;
    status?: string;
    ok?: 'true' | 'false' | '';
    via?: string;
    user_id?: string;
    action?: string;
    job_run_id?: string;
    run_id?: string;
    from?: string;
    to?: string;
    min_ms?: number | string;
    max_ms?: number | string;
    limit?: number;
    skip?: number;
}

export interface WorkflowRunPage {
    items: WorkflowRunRecord[];
    total: number;
    limit: number;
    skip: number;
}

export interface WorkflowRunStats {
    total: number;
    by_workflow: {
        workflow_id: string;
        runs: number;
        failed: number;
        avg_ms?: number | null;
        last_at?: string | null;
    }[];
    by_status: Record<string, number>;
}

export interface IdentityDoc {
    id?: string;
    [k: string]: any;
}

/** A-0017 — one INTERNAL, org-provisioned connection the ENGINE resolves
 * server-side. The discovery record is all the browser ever sees: there is
 * no endpoint, no key, no secret in this shape and none in the body the
 * author submits after picking one.
 *
 * GET /app-engine/internal-connections → { items: InternalConnection[] }.
 * The record is CLOSED — {id, name, source_type, description, provisioned,
 * requires} plus unprovisioned_reason, with an engine-side test asserting no
 * other key can appear. COS is the only entry today; the picker keys off
 * `source_type`, so a second entry is DATA (a new engine row), not a
 * UI rewrite. */
export interface InternalConnection {
    /** The id the CONNECT body carries as `connection.internal`, and the
     * stored document as `connections[].internal`. */
    id: string;
    name: string;
    /** Which connector this entry provisions — matched against the dialog's
     * chosen source type ('cos' today). */
    source_type: string;
    description?: string;
    /** False when the org has no such provisioned connection — the entry is
     * still LISTED (disabled) so the author knows the platform offers it. */
    provisioned: boolean;
    /** Config keys the AUTHOR must still supply, dotted from the STORED
     * document's connection entry — e.g. ["config.bucket"]. Note the
     * asymmetry: the connect args are FLAT (`connection.bucket`); the engine
     * is what nests them under `config`. Never credentials. */
    requires?: string[];
    /** Present only when provisioned === false: why it is unavailable.
     * Confirmed field name — the disabled chip's tooltip keys on it. */
    unprovisioned_reason?: string;
}

/** The connection entry a STORED source carries when it points at an
 * internal, platform-resolved connection — what SourceDetail reads, NOT what
 * the connect body posts (see ConnectSourceRequest's internal cos variant:
 * that one is singular and flat). `internal` names the discovery record;
 * `config` holds ONLY the author's non-secret input. No credential key
 * exists on this shape — the engine resolves those server-side. */
export interface InternalSourceConnection {
    id: string;
    name: string;
    default: boolean;
    /** InternalConnection.id — the engine's server-side lookup key. */
    internal: string;
    config: Record<string, any>;
}

export type ConnectSourceRequest = {
    source_type: 'openapi';
    source_id?: string;
    name?: string;
    spec_url?: string;
    spec_text?: string;
} | {
    source_type: 'mongo';
    source_id?: string;
    name?: string;
    connection: Record<string, any>;
} | {
    source_type: 'redis';
    source_id?: string;
    name?: string;
    connection: Record<string, any>;
    /** Optional key match pattern — scopes the SCAN sample. */
    match?: string;
} | {
    source_type: 'mcp';
    source_id?: string;
    name: string;
    /** The MCP server endpoint URL — the Ingester lists its tools and drafts
     * one capability per tool. */
    url: string;
    /** Optional bearer auth — only sent when the user supplied a token. */
    auth?: {
        method: 'bearer';
        credentials: {
            values: {
                token: string;
            };
        };
    };
} | {
    source_type: 'agent';
    source_id?: string;
    name: string;
    /** The agent's A2A endpoint URL — the Ingester lists its skills and
     * drafts one capability per skill. */
    url: string;
    /** Optional bearer auth — only sent when the user supplied a token. */
    auth?: {
        method: 'bearer';
        credentials: {
            values: {
                token: string;
            };
        };
    };
} | {
    source_type: 'cos';
    source_id?: string;
    name?: string;
    /** Object-storage descriptor — { endpoint_url, bucket, region? }. */
    connection: Record<string, any>;
    /** HMAC access/secret keys — { method:'hmac', credentials:{ values:{
     * access_key, secret_key } } }. Transits like the mongo/redis password;
     * belongs in vault refs beyond dev. */
    auth?: Record<string, any>;
} | {
    /** A-0017 — cos over an INTERNAL, org-provisioned connection.
     *
     * SINGULAR and FLAT, like every other connector's connect args: BeXO's
     * connect_source has taken `connection` (object) + `auth` (object) since
     * the bridge was written, and a plural form for cos alone would be a
     * second grammar inside one endpoint. The engine REFUSES the plural
     * document shape by name.
     *
     * The credential fields are ABSENT, not empty: no access_key, no
     * secret_key, no endpoint — and NO `auth` key at all. The engine
     * resolves the org's COS from Vault via `connection.internal`, then
     * stores the author's input as `connections[0].config.bucket`. */
    source_type: 'cos';
    source_id?: string;
    name?: string;
    connection: {
        /** InternalConnection.id — the server-side lookup key. */
        internal: string;
        /** The author's one non-secret input, FLAT (not under `config`). */
        bucket?: string;
        /** Optional, and only when a surface already collects it. */
        region?: string;
    };
};

export interface AppListEntry {
    id: string; // the app doc's 8-pointer id (GET /apps)
    name?: string;
    [k: string]: any;
}
